import {
  handleCreateRmaTicket,
  handleValidateContact,
  handleValidateNotificationEmail,
  toOptionValueInput,
  useRmaStore,
} from '@/entities/rma';
import { useSupportStore } from '@/entities/support';
import {
  isFacilitySubscribeProtectPlus,
  isFacilitySubscribeRenewalStock,
  facilitySelection,
} from '@/entities/facility';
import { useLoadingStore } from '@/shared/model';
import { LOADING_TYPES, OTHER_RMA_REASONS, PATIENT_IMPACT, PROGRAM_REASON } from '@/shared/config';
import {
  showMessageError,
  showMessageSuccess,
  handleFetchCountries,
  preventAppScroll,
} from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'ttag';

dayjs.extend(isSameOrBefore);

/**
 * Backs the New RMA page with its facility selection, contacts, device rows, notification emails and submit flow. Loads the countries on mount, preselects the facility from the router state or local storage while deriving the Protect+ and renewal-stock flags, prefills device rows for a program-initiated RMA, and tracks page scroll.
 *
 * @returns The New RMA form state, refs and the field, submit and address handlers.
 */
const useNewRma = () => {
  const { state: locationState } = useLocation();

  const navigate = useNavigate();

  /**
   * Reads the facilities the signed-in user supports from the support store, which feed the New RMA facility selector. Subscribing to that slice makes the page re-derive its facility options whenever the list loads or changes.
   *
   * @returns The supported facility list from the support store.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  /**
   * Reads the loading store's setter for the global loading text, used to raise and clear the page overlay while an RMA request is validated or submitted. Called with a loading type to show the overlay and with no argument to clear it.
   *
   * @returns The loading-text setter from the loading store.
   */
  const setLoadingPage = useLoadingStore((s) => s.setLoadingText);

  const maxDevicePerTicket = useRmaStore((s) => s.rmaLimitConfig.maxDevicePerTicket);

  const [state, setState] = useMergeState<any>({
    isScroll: false,
    isOpenConfirmModal: false,
    isSaving: false,
    selectedFacility: undefined,
    hasRenewalStock: false,
    hasProtectPlus: false,
    countries: [],
    errorObject: {},
    errorAddress: {},
    clinicNotes: undefined,
    emailList: [],
    rmaDevices: [],
    emailInputValue: undefined,
    senderContact: undefined,
    recipientContact: undefined,
  });

  const emailRef = useRef<any>(undefined);
  const supportFacilitiesList = useRef<any[]>([]);
  const isSameAsShippingAddressRef = useRef(true);

  /**
   * Flags that the page was opened as a Protect+ or return-for-maintenance RMA rather than a manually created one. True only when the router location state carries both a non-empty device list and a facility.
   *
   * @returns True when the RMA was initiated from a program flow.
   */
  const isProtectPlusProgram =
    !_.isEmpty(locationState?.devices) && !_.isEmpty(locationState?.facility);

  /**
   * Stores the facility picked in the New RMA facility selector under the given state key. Keeps the whole option object carrying label, value and programs, ignoring the raw value argument.
   *
   * @param name - State key to store the facility under.
   * @param value - Raw selected value, unused.
   * @param options - Selected facility option holding label, value and programs.
   * @returns Nothing.
   */
  const onChangeFacility = (name: any, value: any, options: any) => {
    setState({ [name]: options });
  };

  /**
   * Replaces the New RMA notification email list with the addresses accepted by the multi-email input. Clears the email validation errors in the same state write.
   *
   * @param value - Accepted notification email addresses.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    setState({ emailList: value, errorObject: {} });
  };

  /**
   * Writes a plain text field of the New RMA form, such as the clinic notes, into state. The input name is used directly as the state key.
   *
   * @param name - Input name used as the state key.
   * @param value - Text value to store.
   * @returns Nothing.
   */
  const onChangeInputText = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Tracks the notification email text typed but not yet committed to the email list. Clears the email validation errors on every keystroke.
   *
   * @param value - Current text in the email input.
   * @returns Nothing.
   */
  const onInputEmailChange = (value: any) => {
    setState({ emailInputValue: value, errorObject: {} });
  };

  /**
   * Converts the device information rows of the New RMA form into the submission payload shape and stores them as rmaDevices. Rows are only kept when every row reports isComplete, reducing select options to the value and describe the API accepts and normalising dateOfEvent to an ISO string; otherwise an empty list is stored.
   *
   * @param deviceInfoArr - Device rows coming from the device information form.
   * @returns Nothing.
   */
  const onChangeDataDeviceRMA = (deviceInfoArr: any): any => {
    const formatDevicesInfoArray: any = [];
    if (_.every(deviceInfoArr, (item) => item.isComplete)) {
      deviceInfoArr.forEach((item: any) => {
        if (item.isComplete) {
          // Check if all field has data
          formatDevicesInfoArray.push({
            reasons: item.reasons?.map(toOptionValueInput),
            deviceId: item.deviceId?.value,
            replacedDevice: item.replacementDeviceId?.value,
            patientImpact: item.patientImpact ? toOptionValueInput(item.patientImpact) : undefined,
            dateOfEvent: dayjs(item.dateOfEvent).toISOString(),
            deviceModel: item.deviceModel,
            lostDeviceNeedReplacement: _.some(
              item.reasons,
              (r: any) => r.value === OTHER_RMA_REASONS.LOST_DEVICE,
            )
              ? item.lostDeviceNeedReplacement
              : undefined,
          });
        }
      });
    }
    setState({ rmaDevices: formatDevicesInfoArray });
  };

  /**
   * Stores the shipping or returning state/province division list of the New RMA form. The field name arriving from the address component is used directly as the state key.
   *
   * @param name - State key to write, shipping or returning divisions.
   * @param value - Division list to store.
   * @returns Nothing.
   */
  const onChangeDivision = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Stores an edited shipping or returning contact of the New RMA form under the given state key. Remaps the address widget's fields onto the contact shape sent to the API, with address becoming street1 and phoneNumber becoming phone, and clears any pending address errors.
   *
   * @param name - State key to store the contact under, shipping or returning.
   * @param value - Address form values to remap into a contact.
   * @returns Nothing.
   */
  const onChangeDataAddress = (name: any, value: any) => {
    const { address, city, country, email, phoneNumber, state, zip, name: contactName } = value;
    const formatContact = {
      name: contactName,
      street1: address,
      city,
      country,
      state,
      zip,
      phone: phoneNumber,
      email,
    };
    setState({
      [name]: formatContact,
      errorAddress: {},
    });
  };

  /**
   * Submits the assembled New RMA request with its facility, sender and recipient contacts, devices, clinic notes and comma-joined notification emails, then routes to the created ticket. Devices are deep cloned with deviceModel stripped before sending, a success or failure message is shown, and the saving flag and loading overlay are cleared on both paths.
   *
   * @returns Resolves once the request has been submitted or failed.
   */
  const handleSubmitRmaRequest = async () => {
    try {
      const clonedRmaDevices = _.cloneDeep(state.rmaDevices);
      clonedRmaDevices.forEach((x: any) => delete x.deviceModel);
      const input = {
        facilityId: state.selectedFacility.value,
        senderContact: state.senderContact,
        recipientContact: state.recipientContact,
        rmaDevices: clonedRmaDevices,
        clinicNotes: state.clinicNotes?.trim(),
        notificationEmail: state.emailList.join(','),
      };
      const rmaTicket = await handleCreateRmaTicket(input);
      showMessageSuccess({
        content: t`We have received your request and someone from RMA support will be reaching out to you shortly`,
      });
      setState({ isSaving: false });
      setLoadingPage();
      if (rmaTicket?.id) {
        navigate(`/rma/${rmaTicket.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to submit rma request: ', error);
      showMessageError({ content: t`Failed to submit rma request` });
      setState({ isSaving: false });
      setLoadingPage();
    }
  };

  /**
   * Validates the New RMA form before submission, covering the notification emails, the shipping contact, the returning contact when it differs, and that at least one device was added. On success it also rejects tickets whose non-stock-return devices exceed maxDevicePerTicket, then submits directly for Protect+ facilities or opens the confirmation modal; otherwise it stores the errors and toasts each one.
   *
   * @returns Resolves once validation has run.
   */
  const handleValidate = async () => {
    setLoadingPage(LOADING_TYPES.SUBMITTING);

    const {
      emailList,
      emailInputValue,
      senderContact,
      shippingDivisions,
      recipientContact,
      returningDivisions,
      hasProtectPlus,
      rmaDevices,
    } = state;

    const errorObjectEmail = handleValidateNotificationEmail(emailList, emailInputValue);

    const promises = [handleValidateContact(senderContact, shippingDivisions, 'shipping')];
    if (!isSameAsShippingAddressRef.current) {
      promises.push(handleValidateContact(recipientContact, returningDivisions, 'returning'));
    }
    const [senderContactError, recipientContactError] = await Promise.all(promises);

    const invalidRmaDevice = _.isEmpty(rmaDevices);

    if (invalidRmaDevice) {
      showMessageError({
        content: t`Please add at least one device to be able to submit a request.`,
      });
    }

    const contactError = {
      ...senderContactError,
      ...(recipientContactError || []),
    };
    if (_.isEmpty(errorObjectEmail) && _.isEmpty(contactError) && !invalidRmaDevice) {
      const array = [
        OTHER_RMA_REASONS.LOST_DEVICE,
        OTHER_RMA_REASONS.RETURN_TO_STOCK,
        OTHER_RMA_REASONS.CLOSED_ACCOUNT,
      ];
      /**
       * Tallies the RMA devices being submitted by whether any of their reasons falls outside the lost-device, return-to-stock and closed-account list. Produces a lookup keyed by that boolean whose true count is then compared against the per-ticket device limit.
       *
       * @returns Counts keyed by the boolean predicate result.
       */
      const count = _.countBy(rmaDevices, (d) =>
        _.some(d.reasons, (r) => !array.includes(r.value)),
      );
      if (count.true > (maxDevicePerTicket ?? Infinity)) {
        showMessageError({ content: t`The RMA device limit has been reached.` });
        setState({ isSaving: false });
        setLoadingPage();
        return;
      }

      if (hasProtectPlus) {
        handleSubmitRmaRequest();
      } else {
        setLoadingPage();
        setState({ isOpenConfirmModal: true });
      }
    } else {
      setState({
        isSaving: false,
        errorObject: errorObjectEmail,
        errorAddress: contactError,
        isOpenConfirmModal: false,
      });
      _.forEach(contactError, (error) => {
        showMessageError({ content: error });
      });
      _.forEach(errorObjectEmail, (error) => {
        showMessageError({ content: error });
      });
      setLoadingPage();
    }
  };

  /**
   * Builds the pre-filled device rows for a Protect+ or return-for-maintenance RMA from the devices carried in the router location state. Each row is stamped with the current date, no patient impact and either the Protect+ or the return-maintenance reason depending on the facility's coverage; outside that program flow nothing is produced.
   *
   * @returns The initial RMA device rows, or undefined outside the program flow.
   */
  const initialRmaDevices = useMemo(() => {
    if (isProtectPlusProgram) {
      const { devices } = locationState;
      return devices?.map((device: any): any => ({
        dateOfEvent: dayjs().toISOString(),
        deviceId: device.deviceId,
        deviceModel: device.model,
        patientImpact: { value: PATIENT_IMPACT.NONE },
        reasons: [
          {
            value: state.hasProtectPlus
              ? PROGRAM_REASON.PROTECT_PLUS
              : PROGRAM_REASON.RETURN_FOR_MAINTENANCE,
          },
        ],
        replacedDevice: null,
      }));
    }
    return undefined;
  }, [isProtectPlusProgram, state.hasProtectPlus]);

  /**
   * Handles the Submit Request button of the New RMA page by starting form validation. Whether the request is sent or the confirmation modal opens follows from the validation outcome.
   *
   * @returns Nothing.
   */
  const onClickSubmitRequest = () => {
    handleValidate();
  };

  /**
   * Records whether the returning address mirrors the shipping address on the New RMA form. Stored in a ref so toggling it does not re-render the form, and validation later reads the ref to decide whether the returning contact needs checking.
   *
   * @param isSameAsShippingAddress - True when the returning address mirrors the shipping one.
   * @returns Nothing.
   */
  const updateIsSameShippingAddress = (isSameAsShippingAddress: any) => {
    isSameAsShippingAddressRef.current = isSameAsShippingAddress;
  };

  useEffect(() => {
    if (supportFacilities) {
      if (isProtectPlusProgram) {
        const { facility } = locationState;
        setState({
          selectedFacility: {
            label: facility.name,
            value: facility.id,
            programs: facility.program,
          },
        });
      } else {
        /**
         * Derives the alphabetically ordered option list for the facility selector from the supported facilities. Each facility is reduced to label, value and programs, then sorted ascending by label.
         *
         * @returns Facility options sorted by label.
         */
        const formattedSupportFacilities = _.orderBy(
          supportFacilities.map((supportFacility: any) => ({
            label: supportFacility.name,
            value: supportFacility.id,
            programs: supportFacility.programs,
            enabledPrograms: supportFacility.enabledPrograms,
          })),
          ['label'],
          ['asc'],
        );
        const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();

        /**
         * Picks the facility to preselect on the New RMA form, preferring the one remembered in local storage. Falls back to the first entry of the label-ordered option list when the stored facility is not among the supported ones.
         *
         * @returns The facility option to preselect.
         */
        const selectedFacility =
          formattedSupportFacilities.find((x) => x.value === selectedFacilityLocalStorage?.value) ||
          formattedSupportFacilities[0];

        supportFacilitiesList.current = formattedSupportFacilities;
        setState({ selectedFacility });
      }
    }
  }, [supportFacilities]);

  useEffect(() => {
    document.title = t`Biocare Diagnostics - New RMA`;
    preventAppScroll(false);
    /**
     * Loads the reference data the New RMA form needs, fetching the country list. Stores the countries into state, logging and swallowing any failure.
     *
     * @returns Resolves once the reference data has been stored.
     */
    const loadData = async () => {
      try {
        const countriesList = await handleFetchCountries();
        setState({ countries: countriesList });
      } catch (error) {
        console.error('Failed to load data: ', error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (state.selectedFacility) {
      emailRef.current.setEmailList([]);
      /**
       * Looks up the full support-facility record matching the facility currently selected on the form. Its programs are then inspected to decide whether the Protect+ and renewal-stock behaviours apply.
       *
       * @returns The matching support facility record, or undefined.
       */
      const selectedFacilityWithEnabledProtectPlusField = supportFacilities?.find(
        (x: any) => x.id === state.selectedFacility?.value,
      );
      setState({
        hasProtectPlus: isFacilitySubscribeProtectPlus(
          selectedFacilityWithEnabledProtectPlusField?.programs,
        ),
        hasRenewalStock: isFacilitySubscribeRenewalStock(
          selectedFacilityWithEnabledProtectPlusField?.programs,
        ),
        clinicNotes: undefined,
      });
    }
  }, [state.selectedFacility]);

  useEffect(() => {
    /**
     * Tracks whether the New RMA page has scrolled away from the top so its header can gain a shadow. Compares window.scrollY against the isScroll flag and writes state only on a transition.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (window.scrollY === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (window.scrollY !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isScroll]);

  return {
    supportFacilities,
    state,
    setState,
    emailRef,
    supportFacilitiesList,
    isProtectPlusProgram,
    onChangeFacility,
    onChangeEmailList,
    onChangeInputText,
    onInputEmailChange,
    onChangeDataDeviceRMA,
    onChangeDivision,
    onChangeDataAddress,
    handleSubmitRmaRequest,
    initialRmaDevices,
    onClickSubmitRequest,
    updateIsSameShippingAddress,
  };
};

export { useNewRma };
